import React from 'react';
import { Box, Typography, Divider, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

interface StepProps {
    index: string;
    title: string;
    body: string;
    accent: string;
    icon: React.ReactNode;
}

const StepIcon: React.FC<{ accent: string; children: React.ReactNode; pulse?: boolean }> = ({
    accent,
    children,
    pulse,
}) => (
    <Box
        sx={{
            position: 'relative',
            width: 60,
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}
    >
        {pulse && (
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    border: `1px solid ${accent}`,
                    opacity: 0,
                    animation: 'fsrPulse 2.4s cubic-bezier(0.25, 0.1, 0.25, 1) infinite',
                    '@keyframes fsrPulse': {
                        '0%': { transform: 'scale(0.6)', opacity: 0.6 },
                        '100%': { transform: 'scale(1.6)', opacity: 0 },
                    },
                }}
            />
        )}
        <Box
            sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: alpha(accent, 0.08),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: accent,
            }}
        >
            {children}
        </Box>
    </Box>
);

const Step: React.FC<StepProps> = ({ index, title, body, icon }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', px: 1.5 }}>
        <Typography
            sx={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.14em',
                color: 'text.disabled',
                textTransform: 'uppercase',
                mb: 1.25,
            }}
        >
            {index}
        </Typography>
        {icon}
        <Typography
            sx={{
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: 'text.primary',
                mt: 1.5,
                mb: 0.5,
            }}
        >
            {title}
        </Typography>
        <Typography
            sx={{
                fontSize: 12.5,
                color: 'text.secondary',
                lineHeight: 1.45,
                maxWidth: 220,
            }}
        >
            {body}
        </Typography>
    </Box>
);

export const FieldSafetyReportsDiagram: React.FC = () => {
    const theme = useTheme();
    const accent = theme.palette.primary.main;

    return (
        <Box
            sx={{
                py: { xs: 2.5, md: 3.5 },
                px: { xs: 2, md: 4 },
                mb: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.text.primary, 0.02),
                border: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
            }}
        >

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                    columnGap: 0,
                    rowGap: { xs: 3, md: 0 },
                    alignItems: 'start',
                    maxWidth: 880,
                    mx: 'auto',
                    position: 'relative',
                }}
            >
                <Step
                    index="Step 01"
                    title="A report is filed"
                    body="A caregiver flags something at the address they're visiting."
                    accent={accent}
                    icon={
                        <StepIcon accent={accent}>
                            <PersonPinCircleIcon sx={{ fontSize: 22 }} />
                        </StepIcon>
                    }
                />
                <Step
                    index="Step 02"
                    title="Anchored to the map"
                    body="The report sits at the location with a notification radius around it."
                    accent={accent}
                    icon={
                        <StepIcon accent={accent} pulse>
                            <LocationOnIcon sx={{ fontSize: 22 }} />
                        </StepIcon>
                    }
                />
                <Step
                    index="Step 03"
                    title="The next teammate is warned"
                    body="When they enter the radius, a push notification lands on their phone."
                    accent={accent}
                    icon={
                        <StepIcon accent={accent}>
                            <NotificationsActiveIcon sx={{ fontSize: 20 }} />
                        </StepIcon>
                    }
                />
            </Box>

            <Divider sx={{ mt: { xs: 3, md: 4 }, mx: 'auto', width: 48, borderColor: alpha(theme.palette.text.primary, 0.1) }} />

            <Box
                sx={{
                    mt: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.25,
                    color: 'text.secondary',
                }}
            >
                <LockOutlinedIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: 12, lineHeight: 1.5 }}>
                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        Safety Concerns
                    </Box>{' '}
                    aren't broadcast to other caregivers — they appear as Risk Reviews in the Alerts table.
                </Typography>
            </Box>
        </Box>
    );
};

export default FieldSafetyReportsDiagram;
